import { BasicButton, PrimaryButton } from '../../widgets/buttons';
import { Dialog, DialogWindow } from '../../widgets/dialog';
import { removeNotification, showInfoNotification, showNotification } from '../../widgets/notificationToaster';
import { ZTooltip } from '../../widgets/ztooltip';
import React, { FC, useRef, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import PantoneLibraryColorsContainer from '../settings/colorPaletteSettings/pantoneLibrary/pantoneLibraryColorsContainer';
import { pantoneLibraryDialogImportDefaultPaletteMutation } from './__generated__/pantoneLibraryDialogImportDefaultPaletteMutation.graphql';
import { PaletteInput } from './__generated__/printTypeCreatePaletteMutation.graphql';
import { PaletteColorInput } from './__generated__/printTypeUpdatePaletteMutation.graphql';

interface PantoneLibraryDialogProps {
    onClosed: (selectedColors?: PaletteColorInput[]) => void;
    onPaletteImported?: (importedPaletteId: string) => void;
    singleSelection?: boolean;
}

const CustomDialog = styled(DialogWindow)`
    flex: 1;
    max-width: 1300px;
    height: 100%;
    overflow-y: auto;
`;

const DialogContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
`;

const FooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
`;

const FooterButtonsContainer = styled.div`
    display: flex;
    gap: 24px;
    padding-top: 24px;
`;

const DialogHeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
`;

const DialogTitle = styled.h2`
    margin: 0 0 6px 0;
    font-size: 16px;
    font-weight: 600;
    color: '#000000';
`;

const PantoneLibraryDialog: FC<PantoneLibraryDialogProps> = ({ onClosed, onPaletteImported, singleSelection = false }) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [colorsToImport, setColorsToImport] = useState<PaletteColorInput[]>([]);

    const [selectedPalette, setSelectedPalette] = useState<PaletteInput | null>();

    const handleSelectedColorsChange = (colors: PaletteColorInput[]) => {
        setColorsToImport(colors);
    };

    const handleAddColors = () => {
        onClosed(colorsToImport);
    };

    const handleSelectedPaletteChange = (palette: PaletteInput | null) => {
        setSelectedPalette(palette);
    };

    const [importPalette, isImportingPalette] = useMutation<pantoneLibraryDialogImportDefaultPaletteMutation>(graphql`
        mutation pantoneLibraryDialogImportDefaultPaletteMutation($id: ID!) {
            importDefaultPalette(id: $id)
        }
    `);

    const handleImportPaletteClick = () => {
        if (selectedPalette && selectedPalette.id) {
            const importPaletteNotification = showInfoNotification({
                message: T._(`Importing Zakeke default Palette... please don't exit from page.`, 'Admin'),
                dismiss: undefined
            });

            importPalette({
                variables: {
                    id: selectedPalette.id
                },
                onCompleted: (response) => {
                    removeNotification(importPaletteNotification);

                    // If we're in a dialog context (like createOrEditPaletteDialog), call the callback
                    if (onPaletteImported) {
                        onPaletteImported(response.importDefaultPalette);
                    }
                },
                onError: () => {
                    removeNotification(importPaletteNotification);
                    showNotification({
                        type: 'danger',
                        message: T._('Error importing palette.', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                }
            });
        }
    };

    const DialogHeader = () => {
        return (
            <DialogHeaderContainer>
                <DialogTitle>{T._('Pantone Palettes', 'Admin')}</DialogTitle>
                <span>{T._('Select a Pantone palette to view its colors. You can import individual colors or the entire palette.', 'Admin')}</span>
                <span>{T._('Zakeke default colors: 20.091', 'Admin')}</span>
            </DialogHeaderContainer>
        );
    };

    return (
        <Dialog
            titleHtml={<DialogHeader />}
            windowDecorator={CustomDialog}
            footerContent={
                <FooterContainer>
                    <FooterButtonsContainer>
                        {colorsToImport.length === 0 ? (
                            <ZTooltip title={T._('Select at least one color to add', 'Admin')} addContainer={true}>
                                <BasicButton $size='medium' disabled={colorsToImport.length === 0} onClick={handleAddColors}>
                                    {T._(singleSelection ? `Add selected color` : `Add selected colors`, 'Admin')}
                                </BasicButton>
                            </ZTooltip>
                        ) : (
                            <BasicButton $size='medium' disabled={colorsToImport.length === 0} onClick={handleAddColors}>
                                {T._(singleSelection ? `Add selected color` : `Add selected colors`, 'Admin')}
                            </BasicButton>
                        )}
                        {selectedPalette?.isZakekeDefault && (
                            <PrimaryButton $size='medium' onClick={handleImportPaletteClick}>
                                {T._('Import entire palette', 'Admin')}
                            </PrimaryButton>
                        )}
                    </FooterButtonsContainer>
                </FooterContainer>
            }
        >
            <DialogContainer>
                <ScrollableContent ref={scrollContainerRef}>
                    <PantoneLibraryColorsContainer
                        onSelectedColorsChange={handleSelectedColorsChange}
                        scrollContainer={scrollContainerRef}
                        singleSelection={singleSelection}
                        onSelectedPaletteChange={handleSelectedPaletteChange}
                    />
                </ScrollableContent>
            </DialogContainer>
        </Dialog>
    );
};

export default PantoneLibraryDialog;
